import DOMPurify from 'dompurify';
import type { ExamState, Incident } from '../types/domain';
import { observationDurationLabel, summarizeCandidatePresence } from './observations';

export const REPORT_DOWNLOAD_WINDOW_MS = 30_000;
export const CANDIDATE_REPORT_FILENAME = 'examsphere-proctoring-report.pdf';

export function createReportExpiry(now = Date.now()): string {
  return new Date(now + REPORT_DOWNLOAD_WINDOW_MS).toISOString();
}

export function getReportSecondsRemaining(expiresAt: string | undefined, now = Date.now()): number {
  if (!expiresAt) return 0;
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 1000));
}

export function isCurrentSessionReport(state: ExamState): boolean {
  return Boolean(
    state.candidate &&
    state.finishedAt &&
    state.reportSessionId === state.candidate.sessionId
  );
}

export function sanitizeReportText(value: string): string {
  return DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

export function explainIncident(incident: Incident): string {
  const source = incident.source === 'browser' ? 'browser activity' : incident.source === 'audio' ? 'microphone analysis' : 'camera analysis';
  return `${incident.label} was flagged by ${source} as a probabilistic signal for review.`;
}

export function revokeTemporaryReportUrl(url: string | undefined): void {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

export function createCandidateReportElement(state: ExamState): HTMLDivElement {
  const report = document.createElement('div');
  Object.assign(report.style, {
    position: 'fixed', left: '-12000px', top: '0', width: '900px', padding: '32px',
    background: '#080b16', color: '#f8fafc', fontFamily: 'Arial, sans-serif', lineHeight: '1.5'
  });
  report.setAttribute('aria-hidden', 'true');

  const add = (tag: 'h1' | 'h2' | 'p' | 'li', text: string, parent: HTMLElement = report) => {
    const element = document.createElement(tag);
    element.textContent = sanitizeReportText(text);
    element.style.margin = tag === 'h1' ? '0 0 20px' : '10px 0';
    parent.appendChild(element);
  };
  const duration = state.startedAt && state.finishedAt
    ? Math.max(0, Math.round((new Date(state.finishedAt).getTime() - new Date(state.startedAt).getTime()) / 1000))
    : 0;

  add('p', 'ExamSphere AI - Prototype Demo');
  add('h1', 'Candidate Proctoring Report');
  add('h2', 'Session Summary');
  add('p', `Candidate: ${state.candidate?.name ?? ''}`);
  add('p', `Exam code: ${state.candidate?.examCode ?? ''}`);
  add('p', `Started: ${state.startedAt ? new Date(state.startedAt).toLocaleString() : 'Not recorded'}`);
  add('p', `Finished: ${state.finishedAt ? new Date(state.finishedAt).toLocaleString() : 'Not recorded'}`);
  add('p', `Duration: ${Math.floor(duration / 60)}m ${duration % 60}s`);
  add('p', `Answers saved: ${Object.keys(state.answers).length}`);
  add('h2', 'Overall Risk Summary');
  add('p', `Risk score: ${state.metrics.riskScore}/100. ${state.incidents.length} proctoring signals recorded for review.`);
  add('h2', 'Proctoring Incidents');
  if (state.incidents.length) {
    const list = document.createElement('ol');
    state.incidents.forEach((incident) => add(
      'li',
      `${incident.label} - ${incident.severity} - ${incident.confidence}% confidence - ${new Date(incident.timestamp).toLocaleString()}. ${explainIncident(incident)}`,
      list
    ));
    report.appendChild(list);
  } else add('p', 'No proctoring incidents were recorded.');
  const observations = state.observations ?? [];
  const presence = summarizeCandidatePresence(observations);
  add('h2', 'Candidate Presence Summary');
  add('p', `Confirmed absences: ${presence.totalAbsenceCount}. Longest absence: ${observationDurationLabel(presence.longestAbsenceMs)}. Total confirmed absence time: ${observationDurationLabel(presence.totalConfirmedAbsenceMs)}.`);
  add('h2', 'Monitoring Observation Timeline');
  if (observations.length) {
    const list = document.createElement('ol');
    observations.forEach((observation) => add(
      'li',
      `${observation.message} Source: ${observation.source.replace(/-/g, ' ')}. Started: ${new Date(observation.startedAt).toLocaleString()}. ${observation.endedAt ? `Ended: ${new Date(observation.endedAt).toLocaleString()}.` : 'Unresolved at submission.'} Duration: ${Math.round(observation.durationMs / 1000)} seconds.`,
      list
    ));
    report.appendChild(list);
  } else add('p', 'No confirmed monitoring observations were recorded.');
  add('p', 'AI-generated proctoring signals are probabilistic indicators for human review. They are not proof of misconduct and may contain errors or reflect ordinary candidate behavior.');
  return report;
}
